import React from 'react';

import { FlexRow } from '@editor/components/utilities/layouts';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@editor/stores/planSelectionStore';
import { Badge } from '@repo/ui/components/Badge';
import InfoThickIcon from '@repo/ui/components/Icon/InfoThickIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3, P3 } from '@repo/ui/components/Typography';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';

import { usePaymentStatus } from '../../../Payment';
import PromoCode from '../../../PromoCode/PromoCode';
import Tag from '../../../Tag/Tag';
import { specialTheme } from '../../../Tag/theme';
import useBillingPrices from '../../hooks/useBillingPrices';
import PaymentInfo from '../../PaymentInfo/PaymentInfo';
import PlanMembersSelector from '../../PlanMembersSelector/PlanMembersSelector';
import {
  ActionButtonsWrapper,
  RightPanelBlock,
  RightPanelBottomBlock,
  RightPanelFormBlock,
  RightPanelWrapper,
} from '../../styles';
import usePlanPurchaseDetails from '../hooks/usePlanPurchaseDetails';
import usePromoCodeManagement from '../hooks/usePromoCodeManagement';
import PlanIntervalSelector from '../PlanIntervalSelector/PlanIntervalSelector';

import { WarningContainer } from './styles';
import { PlanSelectionDetailsProps } from './types';

const PlanSelectionDetails: React.FC<PlanSelectionDetailsProps> = ({
  currentSubscription,
  actionButtons,
  subscriptionChangePreviewData,
}) => {
  const { title, note, tag, warning, isUpgrade, isChanged } =
    usePlanPurchaseDetails({
      currentSubscription,
      subscriptionChangePreviewData,
    });
  const paymentStatus = usePaymentStatus();
  const { onPromoCodeApplication, onPromoCodeRemoval } =
    usePromoCodeManagement();

  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);
  const prices = useBillingPrices();
  // @NOTE: This key is used to reset the promo code when the prices or promo code changes in the store
  const promoCodeKey = `price-${prices?.[0].id ?? 'default'}-promo-${promoCode?.code ?? 'default'}`;

  const isNewThemeActive = useNewThemeActive();
  let tagContent = null;

  if (tag) {
    tagContent = isNewThemeActive ? (
      <Badge text={tag} variant="blue" />
    ) : (
      <Tag theme={specialTheme}>{tag}</Tag>
    );
  }

  return (
    <RightPanelWrapper>
      <RightPanelBlock showRevampedCheckoutFlow>
        <RightPanelFormBlock data-testid="subscriptionmodal--plan-details">
          <FlexRow alignItems="center" gap="8px">
            {tagContent}
            {title && <H3>{title}</H3>}
          </FlexRow>
          <PlanIntervalSelector currentSubscription={currentSubscription} />
          <FlexRow gap="12px" width="100%">
            <PlanMembersSelector />
          </FlexRow>
        </RightPanelFormBlock>
        <Spacer height="16px" />
        <RightPanelBottomBlock>
          {
            // If user is upgrading a subscription, enable promo code input. Downgrade or editing an existing subscription is not supported.
          }
          {isUpgrade && isChanged && (
            <PromoCode
              key={promoCodeKey}
              disabled={paymentStatus.loading}
              initialValue={promoCode?.code}
              onApplyClick={onPromoCodeApplication}
              onRemoveClick={onPromoCodeRemoval}
            />
          )}
          <Spacer height="12px" />
          {warning && (
            <WarningContainer>
              <InfoThickIcon height="16px" />
              <P3 color={themeStyle.varInkMain}>
                <b>Error!</b> {warning}
              </P3>
            </WarningContainer>
          )}
          <PaymentInfo
            currentSubscription={currentSubscription}
            subscriptionChangePreviewData={subscriptionChangePreviewData}
          />
          {note && (
            <>
              <Spacer height="16px" />
              <P3 color={themeStyle.varInkMedium}>{note}</P3>
            </>
          )}
        </RightPanelBottomBlock>
        <Spacer height="24px" />
      </RightPanelBlock>
      <ActionButtonsWrapper showRevampedCheckoutFlow>
        {actionButtons}
      </ActionButtonsWrapper>
    </RightPanelWrapper>
  );
};

export default PlanSelectionDetails;
